import { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Flex } from 'foundations-components/components/Flex';
import { StatusDot } from 'foundations-components/transitional/components/StatusDot';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { invoiceStatusToStatusDotUse, invoiceStatusToStatusDotMessage } from '../../utils/typeAdapters';
import { buildInvoiceDetailsUrl } from '../../utils/invoiceUrls';
import { useInvoicesData } from '../../hooks/useInvoicesData';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import { trackInteraction } from '../../utils/usageTracker';
import { PaginatedDataTable, DEFAULT_PAGE_SIZE } from '../PaginatedDataTable';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ClickableRow = styled.tr.withConfig({
  displayName: "InvoicesTable__ClickableRow"
})({
  cursor: 'pointer'
});
function InvoiceRowComponent({
  row,
  trRef,
  requiredRowProps,
  children
}) {
  const routerHistory = useHistory();
  return /*#__PURE__*/_jsx(ClickableRow, Object.assign({}, requiredRowProps, {
    ref: trRef,
    "data-test-id": "invoice-row",
    onClick: row.invoiceLink ? () => {
      trackInteraction('invoice-row-clicked');
      routerHistory.push(buildInvoiceDetailsUrl(row));
    } : undefined,
    children: children
  }));
}
const SORTABLE_PROPERTIES = ['invoiceNumber', 'dueDate', 'status', 'amountBilled', 'amountDue'];
const COLUMNS = [{
  columnName: 'invoiceNumber',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "invoices.columns.number"
  }),
  CellComponent: ({
    row
  }) => /*#__PURE__*/_jsx("td", {
    children: row.invoiceNumber
  })
}, {
  columnName: 'dueDate',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "invoices.columns.dueDate"
  }),
  CellComponent: ({
    row
  }) => /*#__PURE__*/_jsx("td", {
    children: typeof row.dueDate === 'number' && /*#__PURE__*/_jsx(FormattedDateTime, {
      value: row.dueDate,
      format: "ll"
    })
  })
}, {
  columnName: 'status',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "invoices.columns.status"
  }),
  CellComponent: ({
    row
  }) => {
    const dotUse = row.status in invoiceStatusToStatusDotUse ? invoiceStatusToStatusDotUse[row.status] : undefined;
    const message = row.status in invoiceStatusToStatusDotMessage ? invoiceStatusToStatusDotMessage[row.status] : undefined;
    return /*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "200",
        children: [dotUse && /*#__PURE__*/_jsx(StatusDot, {
          use: dotUse
        }), message && /*#__PURE__*/_jsx(FormattedMessage, {
          message: message
        })]
      })
    });
  }
}, {
  columnName: 'amountBilled',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "invoices.columns.amountBilled"
  }),
  CellComponent: ({
    row
  }) => /*#__PURE__*/_jsx("td", {
    "data-test-id": "invoice-amount-billed",
    children: /*#__PURE__*/_jsx(FormattedIntlCurrency, {
      value: Number(row.amountBilled),
      currencyCode: row.currencyCode
    })
  })
}, {
  columnName: 'amountDue',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "invoices.columns.balanceDue"
  }),
  CellComponent: ({
    row
  }) => /*#__PURE__*/_jsx("td", {
    "data-test-id": "invoice-amount-due",
    children: /*#__PURE__*/_jsx(FormattedIntlCurrency, {
      value: Number(row.amountDue),
      currencyCode: row.currencyCode
    })
  })
}];
export function InvoicesTable() {
  const [sort, setSort] = useState(undefined);
  const {
    currentOffset,
    currentPage,
    hasPrev,
    handleNextPage,
    handlePrevPage,
    resetPagination
  } = useCursorPagination();
  const sortProperty = SORTABLE_PROPERTIES.find(property => property === (sort === null || sort === void 0 ? void 0 : sort.columnName));
  const sortDirection = sort ? sort.direction === 1 ? 'ASC' : 'DESC' : undefined;
  const {
    loading: invoicesLoading,
    data: invoicesData
  } = useInvoicesData(Object.assign({
    pageLength: DEFAULT_PAGE_SIZE
  }, currentOffset !== undefined && {
    offset: currentOffset
  }, sortProperty !== undefined && {
    sortProperty
  }, sortDirection !== undefined && {
    sortDirection
  }));
  const handleSortsChange = nextSorts => {
    setSort(nextSorts[0]);
    resetPagination();
  };
  const sorts = useMemo(() => sort ? [sort] : [], [sort]);
  const hasMore = Boolean(invoicesData === null || invoicesData === void 0 ? void 0 : invoicesData.hasMore);
  const nextOffset = invoicesData === null || invoicesData === void 0 ? void 0 : invoicesData.offset;
  if ((invoicesData === null || invoicesData === void 0 ? void 0 : invoicesData.results.length) === 0) {
    return /*#__PURE__*/_jsx(EmptyState, {
      flush: true,
      layout: "vertical",
      primaryContent: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoices.empty"
      })
    });
  }
  return /*#__PURE__*/_jsx("div", {
    "data-test-id": "invoices-table",
    children: /*#__PURE__*/_jsx(PaginatedDataTable, {
      columns: COLUMNS,
      rows: (invoicesData === null || invoicesData === void 0 ? void 0 : invoicesData.results) || [],
      getRowId: row => {
        var _row$objectId;
        return String((_row$objectId = row.objectId) !== null && _row$objectId !== void 0 ? _row$objectId : row.invoiceNumber);
      },
      RowComponent: InvoiceRowComponent,
      loading: invoicesLoading,
      currentPage: currentPage,
      hasMore: hasMore,
      hasPrev: hasPrev,
      onNextPage: () => nextOffset && handleNextPage(nextOffset),
      onPrevPage: handlePrevPage,
      sorts: sorts,
      onSortsChange: handleSortsChange
    })
  });
}